import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {ExhibitData} from "types/Exhibit";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import ExhibitForm from "pages/Exhibit/Form";
import Button from "components/Button";
import {EyeIcon} from "@heroicons/react/solid";
import {useOperator} from "context/OperatorContext";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";
import {stripProperties} from "../../../services/object";

function MutateExhibit() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const {id} = useParams();

  const {data: exhibit, isLoading} = useQuery(
    ['exhibits', {id}],
    () => ngage.exhibits.get(Number(id)),
    {enabled: !!id && auth.can('exhibits:write')}
  )

  const store = useMutation((data: ExhibitData) => ngage.exhibits.store(data))
  const update = useMutation((data: ExhibitData) => ngage.exhibits.update(Number(id), data))

  const [previewing, setPreviewing] = useState<boolean>(false);

  // Handle form submission
  async function handleSubmit(values: ExhibitData, {setSubmitting}: FormikHelpers<ExhibitData>) {
    const newValues = stripProperties(values, ['building_id', 'is_indoor']);

    id ? await handleUpdate(newValues) : await handleStore(newValues)
    await invalidate('exhibits.all')

    setSubmitting(false)
    navigate('/exhibits')
  }

  // Handle store mutation
  async function handleStore(values: ExhibitData) {
    try {
      await store.mutateAsync(values)
      notify("success", `${termFor('Exhibit')} Created`, `${values.name} has been created`)
    } catch (error) {
      notify("error", `${termFor('Exhibit')} Not Created`, `${values.name} could not be stored`)
      throw error
    }
  }

  // Handle update mutation
  async function handleUpdate(values: ExhibitData) {
    try {
      await update.mutateAsync(values)
      notify("success", `${termFor('Exhibit')} Updated`, `${values.name} has been updated`)
      await invalidate(['exhibits', {id}])
    } catch (error) {
      notify("error", `${termFor('Exhibit')} Not Updated`, `${values.name} could not be updated`)
      throw error
    }
  }

  // Prevent unauthorised viewing
  if (auth.cannot('exhibits:write')) {
    return <NotAuthorised/>
  }

  // Prepare actions
  const actions = (
    <>
      {auth.can('exhibits:read') &&
        <Button icon={EyeIcon} onClick={() => setPreviewing(true)} disabled={previewing}>Preview</Button>
      }
    </>
  )

  // Prepare breadcrumbs
  const newBreadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Exhibits')]: '/exhibits',
    [`New ${termFor('Exhibit')}`]: '/exhibits/new'
  }
  const editBreadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Exhibits')]: '/exhibits',
    [`Edit ${termFor('Exhibit')}`]: `/exhibits/${id}/edit`
  }

  return (
    <Page
      title={`${id ? 'Edit' : 'New'} ${termFor('Exhibit')}`}
      breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
      actions={actions}
      isLoading={isLoading}>

      <ExhibitForm
        exhibit={exhibit}
        onSubmit={handleSubmit}
        previewing={previewing}
        closePreview={() => setPreviewing(false)}
      />
    </Page>
  );
}

export default MutateExhibit;